/**
 * WebSocket Service for Real-Time Chat
 * Handles connection, authentication, and message handling
 * Uses native WebSocket API for maximum compatibility
 */

class WebSocketService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.userId = null;
    this.companyId = null;
    this.messageHandlers = [];
    this.statusHandlers = [];
    this.connectionHandlers = [];
    this.contactUpdateHandlers = [];
    this.refundHandlers = [];

    // ── Phase 1 additions: stateless transactional + BYO + webhook delivery + external inbox ──
    this.transactionalStatusHandlers = [];
    this.audienceFetchHandlers = [];          // started/progress/completed/failed
    this.webhookDeliveryHandlers = [];        // attempted
    this.webhookSubscriptionHandlers = [];    // disabled/enabled
    this.externalInboxHandlers = [];

    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.pingInterval = null;
  }

  /**
   * Connect to WebSocket server
   * @param {string} userId - Current user ID
   * @param {string} companyId - Current company ID
   * @param {string} wsUrl - WebSocket server URL (default: ws://localhost:4000/ws)
   */
  connect(userId, companyId, wsUrl = 'ws://localhost:4000/ws') {
    return new Promise((resolve, reject) => {
      try {
        this.userId = userId;
        this.companyId = companyId;

        console.log(`🔌 WebSocket: Connecting to ${wsUrl}...`);
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('✅ WebSocket: Connected');

          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.notifyConnectionHandlers(true);

          // Authenticate immediately after connection
          this.authenticate();

          // Start keep-alive ping
          this.startPing();
          
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('❌ WebSocket: Error parsing message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('❌ WebSocket: Connection error:', error);
          reject(error);
        };

        this.ws.onclose = () => {
          console.warn('🔌 WebSocket: Disconnected');
          this.isConnected = false;
          this.stopPing();
          this.notifyConnectionHandlers(false);
          this.attemptReconnect(userId, companyId, wsUrl);
        };
      } catch (error) {
        console.error('❌ WebSocket: Failed to create connection:', error);
        reject(error);
      }
    });
  }

  /**
   * Authenticate WebSocket connection
   * MUST be called immediately after connection
   */
  authenticate() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ WebSocket: Cannot authenticate - socket not open');
      return;
    }

    const authMessage = {
      type: 'AUTHENTICATE',
      userId: this.userId,
      companyId: this.companyId
    };

    this.send(authMessage);
    console.log('📤 WebSocket: Authentication message sent');
  }

  /**
   * Subscribe to specific conversation updates
   * @param {string} conversationId - Conversation ID to subscribe to
   */
  subscribeToConversation(conversationId) {
    if (!this.isConnected) {
      console.warn('⚠️ WebSocket: Not connected - cannot subscribe');
      return;
    }

    this.send({
      type: 'SUBSCRIBE_CONVERSATION',
      conversationId
    });

    console.log(`📥 WebSocket: Subscribed to conversation: ${conversationId}`);
  }

  /**
   * Unsubscribe from conversation updates
   */
  unsubscribeFromConversation(conversationId) {
    if (!this.isConnected) return;

    this.send({
      type: 'UNSUBSCRIBE_CONVERSATION',
      conversationId
    });

    console.log(`📤 WebSocket: Unsubscribed from conversation: ${conversationId}`);
  }

  /**
   * Send raw message through WebSocket
   */
  send(message) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ WebSocket: Not connected - cannot send message');
      return false;
    }

    try {
      this.ws.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('❌ WebSocket: Error sending message:', error);
      return false;
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  handleMessage(message) {
    console.log('📬 WebSocket: Message received -', message.type, message);

    switch (message.type) {
      case 'AUTHENTICATED':
        console.log('✅ WebSocket: Authentication successful');
        break;

      case 'INCOMING_MESSAGE':
        this.handleIncomingMessage(message.data);
        break;

      case 'REPLY_SENT':
        this.handleReplySent(message.data);
        break;

      case 'MESSAGE_STATUS_UPDATE':
        this.handleStatusUpdate(message.data);
        break;

      case 'TYPING_INDICATOR':
        this.handleTypingIndicator(message.data);
        break;

      case 'CONVERSATION_USERS_UPDATE':
        console.log('👥 WebSocket: Contact list update:', message.data);
        this.handleConversationUsersUpdate(message.data);
        break;

      case 'CONVERSATION_MESSAGE_UPDATE':
        console.log('💬 WebSocket: Chat message update:', message.data);
        this.handleConversationMessageUpdate(message.data);
        break;

      case 'MESSAGES_READ':
        console.log('🔵 WebSocket: Messages read by recipient:', message.data);
        this.handleMessagesRead(message.data);
        break;

      case 'CAMPAIGN_REFUND_CREDITED':
        // Deprecated: backend no longer emits this for normal billing flows (plan-quota model).
        // Only handle edge-case all_messages_failed quota restore.
        if (message.data?.reason === 'all_messages_failed') {
          this.notifyHandlers(this.refundHandlers || [], message.data);
        }
        break;

      case 'MESSAGE_REFUND_CREDITED':
        // Deprecated: backend no longer emits this for normal billing flows (plan-quota model).
        if (message.data?.reason === 'all_messages_failed') {
          this.notifyHandlers(this.refundHandlers || [], message.data);
        }
        break;

      case 'PONG':
        console.log('💚 WebSocket: Server is alive');
        break;

      case 'SUBSCRIBED':
        console.log('📥 WebSocket: Subscription confirmed');
        break;

      case 'UNSUBSCRIBED':
        console.log('📤 WebSocket: Unsubscribed');
        break;

      // ── Phase 1 additions: new event types (purely additive) ──
      case 'TRANSACTIONAL_MESSAGE_STATUS':
      case 'transactional.message.status':
        this.notifyHandlers(this.transactionalStatusHandlers, message.data);
        break;

      case 'AUDIENCE_FETCH_STARTED':
      case 'AUDIENCE_FETCH_PROGRESS':
      case 'AUDIENCE_FETCH_COMPLETED':
      case 'AUDIENCE_FETCH_FAILED':
      case 'audience.fetch.started':
      case 'audience.fetch.progress':
      case 'audience.fetch.completed':
      case 'audience.fetch.failed':
        this.notifyHandlers(this.audienceFetchHandlers, {
          phase: String(message.type).toLowerCase().replace(/_/g, '.'),
          ...(message.data || {}),
        });
        break;

      case 'WEBHOOK_DELIVERY_ATTEMPTED':
      case 'webhook.delivery.attempted':
        this.notifyHandlers(this.webhookDeliveryHandlers, message.data);
        break;

      case 'WEBHOOK_SUBSCRIPTION_DISABLED':
      case 'WEBHOOK_SUBSCRIPTION_ENABLED':
      case 'webhook.subscription.disabled':
      case 'webhook.subscription.enabled':
        this.notifyHandlers(this.webhookSubscriptionHandlers, {
          state: String(message.type).toLowerCase().includes('enabled') ? 'enabled' : 'disabled',
          ...(message.data || {}),
        });
        break;

      case 'EXTERNAL_INBOX_MESSAGE':
      case 'external.inbox.message':
        this.notifyHandlers(this.externalInboxHandlers, message.data);
        break;

      default:
        console.warn('⚠️ WebSocket: Unknown message type:', message.type, '- Full payload:', message);
    }
  }

  /**
   * Handle incoming message from user
   */
  handleIncomingMessage(data) {
    console.log('📨 WebSocket: New incoming message:', data);

    // Notify all registered handlers
    this.messageHandlers.forEach(handler => {
      try {
        handler({
          type: 'INCOMING',
          conversationId: data.conversationId,
          phoneNumber: data.phoneNumber,
          profileName: data.profileName,
          content: data.messageContent || data.message || data.content,
          timestamp: data.timestamp || data.entrytime,
          messageId: data.messageId || data.conversationId,
          // Media fields
          msg_type: data.msg_type,
          mediaUrl: data.mediaUrl,
          isMedia: data.isMedia,
          mediaType: data.mediaType || data.msg_type,
          mediaPreview: data.mediaPreview,
          repliedToTemplate: data.repliedToTemplate,
          replied_to_template: data.replied_to_template
        });
      } catch (error) {
        console.error('❌ WebSocket: Error in message handler:', error);
      }
    });
  }

  /**
   * Handle reply sent confirmation
   * Backend now sends wamid (WhatsApp message ID) — this is the key
   * that links a sent message to later STATUS_UPDATE events.
   */
  handleReplySent(data) {
    console.log('✅ WebSocket: Reply sent confirmation:', data);

    this.messageHandlers.forEach(handler => {
      try {
        handler({
          type: 'REPLY_SENT',
          conversationId: data.conversationId,
          phoneNumber: data.phoneNumber || data.mobileno,
          content: data.messageContent || data.message,
          timestamp: data.timestamp,
          messageId: data.messageId,
          // Key field — store this to match future MESSAGE_STATUS_UPDATE events
          wamid: data.wamid,
          // Media fields
          msg_type: data.msg_type,
          mediaUrl: data.mediaUrl,
          mediaType: data.mediaType || data.msg_type,
        });
      } catch (error) {
        console.error('❌ WebSocket: Error in message handler:', error);
      }
    });
  }

  /**
   * Handle message status updates (sent, delivered, read)
   * Backend sends statusCode (1=sent, 3=delivered, 4=read, 2=failed)
   * and/or a string status field. We normalise both into a string.
   */
  handleStatusUpdate(data) {
    // Normalize field names — backend may use different keys
    const phoneNumber = data.phoneNumber || data.mobileno || data.mobile_no || data.phone;
    const wamid = data.wamid || data.whatsappMessageId || data.waba_message_id || data.messageId || data.message_id;

    // Map numeric statusCode → string, fall back to raw string field
    const STATUS_CODE_MAP = { 1: 'sent', 2: 'failed', 3: 'delivered', 4: 'read' };
    const status = STATUS_CODE_MAP[data.statusCode]
      || data.status
      || data.message_status
      || data.delivery_status;

    console.log('📊 WebSocket: Message status update -', status,
      '(code:', data.statusCode, ') phone:', phoneNumber, 'wamid:', wamid);

    // Forward campaignId so BroadcastPage can update the correct campaign row in real-time
    const campaignId = data.campaignId || data.campaign_id || data.campaignid || null;

    this.statusHandlers.forEach(handler => {
      try {
        handler({
          phoneNumber,
          whatsappMessageId: wamid,
          wamid,
          status, // 'sent' | 'delivered' | 'read' | 'failed'
          statusCode: data.statusCode,
          campaignId,
          timestamp: data.timestamp
        });
      } catch (error) {
        console.error('❌ WebSocket: Error in status handler:', error);
      }
    });
  }

  /**
   * Handle typing indicators
   */
  handleTypingIndicator(data) {
    console.log(data.isTyping ? '✏️ WebSocket: User is typing...' : '✋ WebSocket: User stopped typing');
    // Implement typing indicator UI in component
  }

  /**
   * Handle conversation users update (contact list sidebar)
   */
  handleConversationUsersUpdate(data) {
    console.log('👥 WebSocket: Contact list update:', data);

    this.contactUpdateHandlers.forEach(handler => {
      try {
        handler({
          type: 'CONVERSATION_USERS_UPDATE',
          username: data.username,
          mobileno: data.mobileno,
          lastmessage: data.lastmessage,
          lastmessagetime: data.lastmessagetime,
          campaignid: data.campaignid,
          templateid: data.templateid,
          unreadcount: data.unreadcount,
          flag: data.flag, // 0 = incoming, 1 = outgoing
          repliedToTemplate: data.repliedToTemplate,
          replied_to_template: data.replied_to_template
        });
      } catch (error) {
        console.error('❌ WebSocket: Error in contact update handler:', error);
      }
    });
  }

  /**
   * Handle conversation message update (active chat panel)
   */
  handleConversationMessageUpdate(data) {
    console.log('💬 WebSocket: Conversation message update:', data);

    this.messageHandlers.forEach(handler => {
      try {
        handler({
          type: 'CONVERSATION_MESSAGE_UPDATE',
          conversationId: data.conversationId,
          mobileno: data.mobileno,
          phoneNumber: data.mobileno,
          username: data.username,
          profileName: data.username,
          content: data.message,
          timestamp: data.entrytime,
          campaignid: data.campaignid,
          templateid: data.templateid,
          isread: data.isread,
          flag: data.flag, // 0 = incoming, 1 = outgoing
          // Media fields
          msg_type: data.msg_type,
          mediaUrl: data.mediaUrl,
          isMedia: data.isMedia,
          mediaType: data.mediaType || data.msg_type,
          mediaPreview: data.mediaPreview,
          repliedToTemplate: data.repliedToTemplate,
          replied_to_template: data.replied_to_template
        });
      } catch (error) {
        console.error('❌ WebSocket: Error in message handler:', error);
      }
    });
  }

  /**
   * Handle bulk messages_read event — recipient opened the conversation.
   * Dispatches a MESSAGES_READ type through statusHandlers so InboxPage
   * can flip all outgoing messages in that conversation to status 'read'.
   */
  handleMessagesRead(data) {
    this.statusHandlers.forEach(handler => {
      try {
        handler({
          type: 'MESSAGES_READ',
          phoneNumber: data.phoneNumber || data.mobileno,
          conversationId: data.conversationId || data.conversationUuid,
        });
      } catch (error) {
        console.error('❌ WebSocket: Error in status handler (MESSAGES_READ):', error);
      }
    });
  }

  /**
   * Handle refund events (campaign_refund or message_refund)
   * Dispatches refund data to all registered refund handlers
   * @param {Object} data - Refund event data from backend
   * @param {string} refundType - 'campaign_refund' or 'message_refund'
   */
  handleRefundEvent(data, refundType) {
    const refundPayload = {
      type: refundType,
      campaignId: data.campaignId || data.campaign_id,
      campaignName: data.campaignName || data.campaign_name,
      // Legacy wallet-based fields (kept for backward compat)
      refundedAmount: parseFloat(data.refundedAmount || data.refunded_amount || 0),
      newBalance: parseFloat(data.newBalance || data.new_balance || 0),
      // Plan-based quota restoration fields
      restoredCount: parseInt(data.restoredCount || data.restored_count || data.failedCount || data.failed_count || 0),
      failedCount: parseInt(data.failedCount || data.failed_count || 0),
      channel: data.channel || 'whatsapp',
      transactionId: data.transactionId || data.transaction_id,
      timestamp: data.timestamp || new Date().toISOString(),
    };

    console.log('💰 WebSocket: Processing refund event:', refundPayload);

    this.refundHandlers.forEach(handler => {
      try {
        handler(refundPayload);
      } catch (error) {
        console.error('❌ WebSocket: Error in refund handler:', error);
      }
    });
  }

  /**
   * Emit mark_as_read to backend.
   * Backend will fire MESSAGES_READ back to the original sender,
   * causing their frontend to flip all messages to 'read' (blue tick).
   * @param {string} phoneNumber - The phone number / conversation identifier
   */
  markAsRead(phoneNumber) {
    if (!this.isConnected) return;
    this.send({
      type: 'MARK_AS_READ',
      phoneNumber,
      companyId: this.companyId
    });
    console.log('✅ WebSocket: mark_as_read emitted for', phoneNumber);
  }

  /**
   * Register callback for contact updates
   * @param {function} handler - Callback function(contactData)
   */
  onContactUpdate(handler) {
    if (typeof handler === 'function') {
      this.contactUpdateHandlers.push(handler);
      console.log('📌 WebSocket: Contact update handler registered');
    }
  }

  /**
   * Register callback for refund events
   * @param {function} handler - Callback function(refundData)
   */
  onRefundUpdate(handler) {
    if (typeof handler === 'function') {
      this.refundHandlers.push(handler);
      console.log('📌 WebSocket: Refund update handler registered');
    }
  }

  /**
   * Register callback for incoming messages
   * @param {function} handler - Callback function(messageData)
   */
  onMessage(handler) {
    if (typeof handler === 'function') {
      this.messageHandlers.push(handler);
      console.log('📌 WebSocket: Message handler registered');
    }
  }

  /**
   * Register callback for status updates
   * @param {function} handler - Callback function(statusData)
   */
  onStatusUpdate(handler) {
    if (typeof handler === 'function') {
      this.statusHandlers.push(handler);
      console.log('📌 WebSocket: Status update handler registered');
    }
  }

  /**
   * Register callback for connection status changes
   * @param {function} handler - Callback function(isConnected)
   */
  onConnectionChange(handler) {
    if (typeof handler === 'function') {
      this.connectionHandlers.push(handler);
      console.log('📌 WebSocket: Connection change handler registered');
    }
  }

  /**
   * Start keep-alive ping
   */
  startPing() {
    if (this.pingInterval) clearInterval(this.pingInterval);
    
    this.pingInterval = setInterval(() => {
      if (this.isConnected) {
        this.send({ type: 'PING' });
      }
    }, 30000); // Every 30 seconds
  }

  /**
   * Stop keep-alive ping
   */
  stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Notify all connection handlers
   */
  notifyConnectionHandlers(isConnected) {
    this.connectionHandlers.forEach(handler => {
      try {
        handler(isConnected);
      } catch (error) {
        console.error('❌ WebSocket: Error in connection handler:', error);
      }
    });
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  attemptReconnect(userId, companyId, wsUrl) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ WebSocket: Max reconnection attempts reached');
      return;
    }

    const delay = Math.pow(2, this.reconnectAttempts) * this.reconnectDelay;
    this.reconnectAttempts++;

    console.log(
      `⏳ WebSocket: Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})...`
    );

    setTimeout(() => {
      this.connect(userId, companyId, wsUrl).catch(error => {
        console.error('❌ WebSocket: Reconnection failed:', error);
      });
    }, delay);
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    this.stopPing();
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.isConnected = false;
    console.log('🔌 WebSocket: Disconnected');
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      userId: this.userId,
      companyId: this.companyId,
      reconnectAttempts: this.reconnectAttempts,
      readyState: this.ws ? this.ws.readyState : null
    };
  }

  /**
   * Clear all registered handlers
   */
  clearHandlers() {
    this.messageHandlers = [];
    this.statusHandlers = [];
    this.connectionHandlers = [];
    this.contactUpdateHandlers = [];
    this.refundHandlers = [];
    // Phase 1 additions
    this.transactionalStatusHandlers = [];
    this.audienceFetchHandlers = [];
    this.webhookDeliveryHandlers = [];
    this.webhookSubscriptionHandlers = [];
    this.externalInboxHandlers = [];
    console.log('🗑️ WebSocket: All handlers cleared');
  }

  // ── Phase 1 additions: generic notify + new event registrars ──

  notifyHandlers(handlerList, data) {
    if (!Array.isArray(handlerList)) return;
    handlerList.forEach((h) => {
      try { h(data); } catch (err) { console.error('WS handler error:', err); }
    });
  }

  onTransactionalStatus(handler) {
    if (typeof handler === 'function') this.transactionalStatusHandlers.push(handler);
  }

  onAudienceFetch(handler) {
    if (typeof handler === 'function') this.audienceFetchHandlers.push(handler);
  }

  onWebhookDelivery(handler) {
    if (typeof handler === 'function') this.webhookDeliveryHandlers.push(handler);
  }

  onWebhookSubscription(handler) {
    if (typeof handler === 'function') this.webhookSubscriptionHandlers.push(handler);
  }

  onExternalInbox(handler) {
    if (typeof handler === 'function') this.externalInboxHandlers.push(handler);
  }
}

// Export singleton instance
const webSocketServiceInstance = new WebSocketService();
export default webSocketServiceInstance;
